import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import syepMain from "../../Assets/syepMain.png";
import cf2025 from "../../Assets/syep_2025.jpeg";
import ambassadorPortrait from "../../Assets/syep14.png";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const sliderImages = [
  { src: syepMain, alt: "Ambassador program highlight" },
  { src: cf2025, alt: "Ambassador community moment" },
];

const eligibility = [
  "Are curious about technology development, STEM, and ethical innovation",
  "Enjoy collaborating and leading projects",
  "Want to make a real impact in your community",
];

const initiatives = [
  {
    title: "Tech Skill-Building Sessions",
    text: "Training in coding, electronics, robotics, and digital tools to strengthen leadership and technical expertise.",
  },
  {
    title: "STEM Workshop Series",
    text: "Designing and running monthly workshops for kids and teens.",
  },
  {
    title: "Community Tech Help Days",
    text: "Assisting community members and local groups with technology challenges.",
  },
  {
    title: "Tech Awareness Campaigns",
    text: "Promoting digital literacy, responsible tech use, and ethical innovation.",
  },
  {
    title: "Mini STEM Project Showcases",
    text: "Presenting and mentoring youth-led projects.",
  },
  {
    title: "Community Engagement",
    text: "Recruiting participants, promoting opportunities, and sharing Choicest Fruit programs with community members.",
  },
];

const benefits = [
  "Hands-on experience in responsible technology development and project leadership",
  "Opportunities to teach and mentor younger students and seniors",
  "Recognition for community service hours and potential stipends",
  "A chance to make a tangible difference while shaping the future of ethical technology",
];

function Ambassadors() {
  const navigate = useNavigate();

  const handleApply = () => {
    navigate("/registration#ambassador-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2400,
    pauseOnHover: true,
    adaptiveHeight: true,
  };

  const videoId = "5mPU-mIQgoQ";

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: initiativesRef, hasEntered: initiativesSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });

  return (
    <Container fluid className="about-section ambassadors-page">
      <Particle />

      <Container className="ambassadors-container">
        <header className="ambassadors-hero">
          <h1 className="ambassadors-hero-title">
            Our <strong className="purple">Ambassadors</strong>
          </h1>
          <p className="ambassadors-hero-lead">
            Youth leaders, tech innovators, and community changemakers who help expand access to technology education and service.
          </p>
          <div className="ambassadors-hero-cta">
            <Button onClick={handleApply} className="custom-yellow-btn ambassadors-apply-btn">
              Apply Now
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`ambassadors-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="ambassadors-top" style={{ justifyContent: "center" }}>
            <Col md={7} className="ambassadors-left">
              <div className="policy-card ambassadors-slider-card" aria-label="Ambassador program highlights">
                <Slider {...settings}>
                  {sliderImages.map((image, index) => (
                    <div key={index} className="ambassadors-slide">
                      <img src={image.src} alt={image.alt} className="ambassadors-slide-img" />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>

            <Col md={5} className="ambassadors-right">
              <div className="policy-card ambassadors-summary-card">
                <h2 className="ambassadors-card-title">Ambassador Program</h2>
                <p className="about-text ambassadors-card-text">
                  The Ambassador Program is a systematic, localized effort to foster community life and advance social well-being
                  through tech development, training, and service-oriented activities.
                </p>
                <ul className="ambassadors-bullets">
                  <li>For passionate youth ages 14–24</li>
                  <li>Build leadership through hands-on tech</li>
                  <li>Serve neighborhoods with practical support</li>
                </ul>

                <Button onClick={handleApply} className="custom-yellow-btn ambassadors-apply-btn ambassadors-apply-btn-secondary">
                  Apply Here
                </Button>
              </div>

              <div className="policy-card ambassadors-image-card" aria-label="Ambassador portrait">
                <Tilt glareEnable={false}>
                  <img src={ambassadorPortrait} className="img-fluid ambassadors-image" alt="Choicest Fruit ambassador" />
                </Tilt>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`ambassadors-section ambassadors-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label="Ambassador program overview"
        >
          <Row>
            <Col md={6} className="ambassadors-col">
              <div className="policy-card ambassadors-copy-card">
                <h2 className="ambassadors-section-heading">Who Can Be an Ambassador?</h2>
                <p className="ambassadors-body">
                  We welcome passionate youth ages 14–24 who:
                </p>
                <ul className="ambassadors-list">
                  {eligibility.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col md={6} className="ambassadors-col">
              <div className="policy-card ambassadors-copy-card">
                <h2 className="ambassadors-section-heading">Why Become an Ambassador?</h2>
                <p className="ambassadors-body">Being a Choicest Fruit Ambassador offers youth:</p>
                <ul className="ambassadors-list">
                  {benefits.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="ambassadors-body ambassadors-body-muted">
                  At the heart of it all is service: responding to the needs of others, uplifting neighborhoods, and contributing
                  to the betterment of humanity.
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={initiativesRef}
          className={`ambassadors-section ambassadors-reveal${initiativesSeen ? " is-seen" : ""}`}
          aria-label="Ambassador initiatives"
        >
          <h2 className="ambassadors-section-heading ambassadors-section-heading-center">What Ambassadors Do</h2>
          <p className="ambassadors-lead">
            Ambassadors take part in initiatives designed to strengthen tech skills and community life.
          </p>

          <div className="ambassadors-grid">
            {initiatives.map((initiative) => (
              <div key={initiative.title} className="policy-card ambassadors-feature-card">
                <h3 className="ambassadors-feature-title">{initiative.title}</h3>
                <p className="ambassadors-feature-text">{initiative.text}</p>
              </div>
            ))}
          </div>

          <p className="ambassadors-footnote">
            These initiatives are carried out within neighborhoods and manageable geographic areas, ensuring efforts are sustained,
            coordinated, and inclusive.
          </p>
        </section>

        <section
          ref={videoRef}
          className={`ambassadors-section ambassadors-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label="Ambassador program video"
        >
          <h2 className="ambassadors-section-heading ambassadors-section-heading-center">Summer Internship Program</h2>
          <div className="policy-card ambassadors-video-card">
            <div className="ambassadors-video">
              <iframe
                title="Choicest Fruit internship program overview"
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`ambassadors-testimonials-section ambassadors-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label="Ambassador testimonials"
        >
          <h2 className="ambassadors-section-heading ambassadors-section-heading-center">Testimonials</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default Ambassadors;
